import { motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import type { ConversationData, ChatUser } from './types';
import { getConversationTitle, getConversationSubtitle, getConversationIcon, formatTime } from './utils';
import { useT } from '@/lib/i18n';

interface ConversationListProps {
  conversations: ConversationData[];
  selectedConversation: ConversationData | null;
  userId: string | null;
  userCache: Record<string, ChatUser>;
  onSelect: (conversation: ConversationData | null) => void;
}

export function ConversationList({
  conversations,
  selectedConversation,
  userId,
  userCache,
  onSelect,
}: ConversationListProps) {
  const { t } = useT();

  // Which pane is on screen is a media query, not a measurement: reading
  // `window.innerWidth` during render decided it once, at mount, and then never
  // again — so a window crossing the breakpoint (or a rotation) kept whichever
  // answer it happened to start with. On md+ both panes are always mounted.
  return (
    <div
      className={`${
        selectedConversation ? 'hidden md:flex' : 'flex'
      } flex-1 min-h-0 md:flex-none flex-col w-full md:w-96 lg:w-128 md:bg-white/30 md:dark:bg-neutral-900/30 border-r border-divider overflow-hidden`}
    >
      {/* Inbox Header. The safe-area padding sits on the bar itself so its
          background reaches under the status bar — mobile only, since on md+
          the app's own nav is above this. */}
      <div className="flex-none max-md:top-safe-padding border-b border-divider max-md:dark:bg-neutral-900/80 max-md:bg-white/60">
        <div className="p-4">
          <h1 className="text-xl font-bold flex items-center gap-2 justify-center">
            <MessageCircle className="w-5 h-5 text-primary-500" />
            {t('chat.title')}
          </h1>
        </div>
      </div>

      {/* Conversation List. The floating mobile nav-bar sits over the bottom of
          this, so the last row needs room to clear it. */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain pb-above-nav-bar">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <p className="text-sm text-dark-muted">No chats yet</p>
          </div>
        ) : (
          conversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              isSelected={selectedConversation?.id === conversation.id}
              userId={userId}
              userCache={userCache}
              onSelect={onSelect}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface ConversationItemProps {
  conversation: ConversationData;
  isSelected: boolean;
  userId: string | null;
  userCache: Record<string, ChatUser>;
  onSelect: (conversation: ConversationData) => void;
}

function ConversationItem({
  conversation,
  isSelected,
  userId,
  userCache,
  onSelect,
}: ConversationItemProps) {
  const { t } = useT();
  const isGroupChat = conversation.kind === 'group';
  const unreadCount = isGroupChat ? 0 : conversation.unreadCounts?.[userId || ''] || 0;
  const subtitle = getConversationSubtitle(conversation);
  const Icon = getConversationIcon(conversation);

  return (
    <motion.button
      onClick={() => onSelect(conversation)}
      className={`w-full p-4 flex items-start gap-3 border-b border-divider text-left transition-colors ${
        isSelected ? 'bg-white/80 dark:bg-neutral-900/80' : 'hover:bg-primary-50/80 dark:hover:bg-neutral-900/80'
      }`}
      whileTap={{ scale: 0.98 }}
    >
      {/* Icon */}
      <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h4
            className={`font-semibold truncate ${
              unreadCount > 0 ? 'text-primary-600' : ''
            }`}
          >
            {getConversationTitle(conversation, userId, userCache)}
          </h4>

          {unreadCount > 0 && (
            <span className="bg-primary-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        {subtitle && (
          <p className="text-xs text-dark-muted truncate">{subtitle}</p>
        )}
        <div className="flex items-center justify-between gap-2 mt-0.5">
          <p
            className={`text-sm truncate ${
              unreadCount > 0 ? 'font-medium' : 'text-dark-muted'
            }`}
          >
            {conversation.lastMessage?.text || t('chat.empty.title')}
          </p>
          {conversation.lastMessage?.createdAt && (
            <span className="text-xs text-dark-muted flex-shrink-0">
              {formatTime(conversation.lastMessage.createdAt)}
            </span>
          )}
        </div>
      </div>
    </motion.button>
  );
}
